import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { ncsubtypeApi, NCSubtype, NCSubtypeSearchParams } from '../../services/ncsubtypeApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setNCTypes,
  setNCTypesLoading,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  FETCH_NCSUBTYPES,
  FETCH_NCSUBTYPE_BY_ID,
  CREATE_NCSUBTYPE,
  UPDATE_NCSUBTYPE,
  DELETE_NCSUBTYPE,
  SEARCH_NCSUBTYPES,
  FETCH_STATISTICS,
  FETCH_NCTYPES,
  FETCH_BY_NCTYPE_ID,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/ncsubtypeSlice';

// Worker Sagas
function* fetchNCSubtypesSaga(action: PayloadAction<NCSubtypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const response = yield call(ncsubtypeApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to fetch NC Subtypes'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchNCSubtypeByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const ncsubtype = yield call(ncsubtypeApi.getById, action.payload);
    yield put(setSelectedItem(ncsubtype));
  } catch (error: any) {
    console.error('Error fetching NC Subtype:', error);
    yield put(setError(error.message || 'Failed to fetch NC Subtype'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createNCSubtypeSaga(action: PayloadAction<Omit<NCSubtype, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncsubtypeApi.validation.validateNCSubtype, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newNCSubtype = yield call(ncsubtypeApi.create, action.payload);
    yield put(addItem(newNCSubtype));
    yield put(setSuccessMessage('NC Subtype created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating NC Subtype:', error);
    yield put(setError(error.message || 'Failed to create NC Subtype'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateNCSubtypeSaga(action: PayloadAction<{ id: number; ncsubtype: Partial<NCSubtype> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, ncsubtype } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(ncsubtypeApi.validation.validateNCSubtype, ncsubtype);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedNCSubtype = yield call(ncsubtypeApi.update, id, ncsubtype);
    yield put(updateItem(updatedNCSubtype));
    yield put(setSuccessMessage('NC Subtype updated successfully'));
  } catch (error: any) {
    console.error('Error updating NC Subtype:', error);
    yield put(setError(error.message || 'Failed to update NC Subtype'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteNCSubtypeSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(ncsubtypeApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('NC Subtype deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete NC Subtype'));
    }
  } catch (error: any) {
    console.error('Error deleting NC Subtype:', error);
    yield put(setError(error.message || 'Failed to delete NC Subtype'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchNCSubtypesSaga(action: PayloadAction<NCSubtypeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(ncsubtypeApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to search NC Subtypes'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(ncsubtypeApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

function* fetchNCTypesSaga(): Generator<any, void, any> {
  try {
    yield put(setNCTypesLoading(true));
    const ncTypes = yield call(ncsubtypeApi.getNCTypes);
    yield put(setNCTypes(ncTypes));
  } catch (error: any) {
    console.error('Error fetching NC Types:', error);
    yield put(setError(error.message || 'Failed to fetch NC Types'));
  } finally {
    yield put(setNCTypesLoading(false));
  }
}

function* fetchByNCTypeIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const ncsubtypes = yield call(ncsubtypeApi.getByNCTypeId, action.payload);
    yield put(setItems(ncsubtypes));
  } catch (error: any) {
    console.error('Error fetching NC Subtypes by NC Type ID:', error);
    yield put(setError(error.message || 'Failed to fetch NC Subtypes'));
  } finally {
    yield put(setLoading(false));
  }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncsubtypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncsubtypeApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully activated ${action.payload.length} NC Subtypes`));
      // Refresh the list to show updated status
      yield call(fetchNCSubtypesSaga, { payload: undefined, type: FETCH_NCSUBTYPES });
    } else {
      yield put(setError(response.message || 'Failed to activate NC Subtypes'));
    }
  } catch (error: any) {
    console.error('Error bulk activating NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to activate NC Subtypes'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncsubtypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncsubtypeApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deactivated ${action.payload.length} NC Subtypes`));
      // Refresh the list to show updated status
      yield call(fetchNCSubtypesSaga, { payload: undefined, type: FETCH_NCSUBTYPES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate NC Subtypes'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to deactivate NC Subtypes'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(ncsubtypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(ncsubtypeApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deleted ${action.payload.length} NC Subtypes`));
      // Refresh the list to show updated items
      yield call(fetchNCSubtypesSaga, { payload: undefined, type: FETCH_NCSUBTYPES });
    } else {
      yield put(setError(response.message || 'Failed to delete NC Subtypes'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to delete NC Subtypes'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* exportToCSVSaga(action: PayloadAction<NCSubtypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const blob = yield call(ncsubtypeApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ncsubtypes-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('NC Subtypes exported successfully'));
  } catch (error: any) {
    console.error('Error exporting NC Subtypes:', error);
    yield put(setError(error.message || 'Failed to export NC Subtypes'));
  } finally {
    yield put(setLoading(false));
  }
}

// Watcher Sagas
function* watchFetchNCSubtypes(): Generator<any, void, any> {
  yield takeLatest(FETCH_NCSUBTYPES, fetchNCSubtypesSaga);
}

function* watchFetchNCSubtypeById(): Generator<any, void, any> {
  yield takeEvery(FETCH_NCSUBTYPE_BY_ID, fetchNCSubtypeByIdSaga);
}

function* watchCreateNCSubtype(): Generator<any, void, any> {
  yield takeEvery(CREATE_NCSUBTYPE, createNCSubtypeSaga);
}

function* watchUpdateNCSubtype(): Generator<any, void, any> {
  yield takeEvery(UPDATE_NCSUBTYPE, updateNCSubtypeSaga);
}

function* watchDeleteNCSubtype(): Generator<any, void, any> {
  yield takeEvery(DELETE_NCSUBTYPE, deleteNCSubtypeSaga);
}

function* watchSearchNCSubtypes(): Generator<any, void, any> {
  yield takeLatest(SEARCH_NCSUBTYPES, searchNCSubtypesSaga);
}

function* watchFetchStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATISTICS, fetchStatisticsSaga);
}

function* watchFetchNCTypes(): Generator<any, void, any> {
  yield takeLatest(FETCH_NCTYPES, fetchNCTypesSaga);
}

function* watchFetchByNCTypeId(): Generator<any, void, any> {
  yield takeLatest(FETCH_BY_NCTYPE_ID, fetchByNCTypeIdSaga);
}

function* watchBulkActivate(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
}

function* watchBulkDeactivate(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
}

function* watchBulkDelete(): Generator<any, void, any> {
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
}

function* watchExportToCSV(): Generator<any, void, any> {
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
}

// Root saga
export function* ncsubtypeSaga(): Generator<any, void, any> {
  yield all([
    watchFetchNCSubtypes(),
    watchFetchNCSubtypeById(),
    watchCreateNCSubtype(),
    watchUpdateNCSubtype(),
    watchDeleteNCSubtype(),
    watchSearchNCSubtypes(),
    watchFetchStatistics(),
    watchFetchNCTypes(),
    watchFetchByNCTypeId(),
    watchBulkActivate(),
    watchBulkDeactivate(),
    watchBulkDelete(),
    watchExportToCSV(),
  ]);
} 